import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BookingStatus } from 'angular/app/core/models/entities/booking';
import { Observable } from 'rxjs';
import { Room } from '../../../core/models/entities/room';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  BookingsDisplayMode,
  BookingsScope,
} from 'angular/app/core/store/bookings/bookings.state';
import { fromStore } from '../../../core/store';
import {
  addDays,
  differenceInMinutes,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { ModalService } from '../../../core/services/modal.service';
import { CreateDuplicateRoomModalComponent } from '../../../rooms/components/create-room-modal/create-duplicate-room-modal.component';
import { CalendarRoomsService } from '../../../core/services/calendar-rooms.service';
import { RoomPermission } from '../../../core/models/entities/permission';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-bookings-calendar-view',
  template: `
    @if (loadingRooms() && !activeRooms().length) {
      <div class="flex h-64 items-center justify-center">
        <app-loader></app-loader>
      </div>
    } @else if (!activeRooms().length) {
      <div class="flex h-64 items-center justify-center">
        <ng-template [appHasPermission]="RoomPermission.Store">
          <app-empty-state
            [message]="emptyRoomsText$ | async"
            icon="house"
            [showAddButton]="noRoomsFilters$ | async"
            (buttonClicked)="createRoom()"
          ></app-empty-state>
        </ng-template>

        <ng-template [appHasNoPermission]="RoomPermission.Store">
          <app-empty-state
            [message]="
              'bookings.calendar-view.no-permission-message' | transloco
            "
            icon="house"
            [showAddButton]="false"
          ></app-empty-state>
        </ng-template>
      </div>
    }

    <div
      class="relative overflow-hidden rounded-xl border border-gray-300"
      [class.hidden]="!activeRooms().length"
    >
      <div class="flex border-b border-gray-300 bg-gray-50">
        <div
          class="flex w-1/5 shrink-0 items-center border-r border-gray-300 px-0.5 sm:px-2"
          #roomsNameContainer
        >
          <button
            type="button"
            cdkOverlayOrigin
            #roomsMenuTrigger="cdkOverlayOrigin"
            class="inline-flex max-w-full min-w-0 cursor-pointer items-center gap-0.5 overflow-hidden rounded-lg px-0.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100 sm:gap-2 sm:px-2"
            [class.bg-gray-100]="isRoomsMenuOpen()"
            (click)="isRoomsMenuOpen.set(!isRoomsMenuOpen())"
          >
            <i class="far fa-filter shrink-0 text-xs text-gray-500"></i>
            <span class="hidden truncate sm:inline">{{
              'bookings.month-view.rooms-title' | transloco
            }}</span>
            <span
              class="shrink-0 rounded-full px-0.5 text-[11px] tabular-nums sm:px-1.5 sm:text-xs"
              [ngClass]="
                rooms().length === activeRooms().length
                  ? 'bg-gray-200 text-gray-600'
                  : 'bg-primary-100 text-primary-700'
              "
              >{{ rooms().length }}/{{ activeRooms().length }}</span
            >
            <span class="hidden sm:inline">
              <i class="far fa-chevron-down text-[10px] text-gray-400"></i>
            </span>
          </button>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="roomsMenuTrigger"
            [cdkConnectedOverlayOpen]="isRoomsMenuOpen()"
            [cdkConnectedOverlayHasBackdrop]="true"
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            cdkConnectedOverlayPanelClass="dropdown-panel"
            [cdkConnectedOverlayOffsetY]="8"
            (backdropClick)="isRoomsMenuOpen.set(false)"
            (detach)="isRoomsMenuOpen.set(false)"
          >
            <div
              class="max-h-96 w-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-md"
            >
              <app-bookings-rooms-sidebar
                [scope]="scope()"
                [showTitle]="false"
                [searchable]="true"
              ></app-bookings-rooms-sidebar>
            </div>
          </ng-template>
        </div>
        <div class="relative h-12 grow" #calendarContainer>
          @if (displayMode() === BookingsDisplayMode.Week) {
            <div class="grid h-full grid-cols-7">
              @for (
                date of currentWeekDates();
                track date;
                let first = $first
              ) {
                <div
                  class="flex items-center justify-center gap-1.5 border-gray-300 text-xs font-medium tracking-wide text-gray-500 uppercase"
                  [class.border-l]="!first"
                >
                  <span>{{ date | dateFns: 'EEE' }}</span>
                  <span
                    class="inline-flex size-6 items-center justify-center rounded-full text-xs normal-case"
                    [ngClass]="{
                      'bg-primary-500 text-white': isToday(date),
                      'text-gray-800': !isToday(date),
                    }"
                    >{{ date | dateFns: 'd' }}</span
                  >
                </div>
              }
            </div>
          } @else {
            @for (hour of dayHourLabels(); track hour.label) {
              <div
                class="absolute top-0 flex h-full -translate-x-1/2 items-center text-xs font-medium text-gray-500 tabular-nums"
                [style.left.%]="hour.left"
              >
                {{ hour.label }}
              </div>
            }
          }
        </div>
      </div>

      <div class="relative">
        @if (!rooms().length) {
          <div class="py-10 text-center text-sm text-gray-500">
            {{ 'bookings.calendar-view.no-room-selected' | transloco }}
          </div>
        }

        @for (room of rooms(); track room.id; let last = $last) {
          <div
            class="group flex min-h-14 border-gray-200"
            [class.border-b]="!last"
            data-cy="room-row"
          >
            <app-booking-calendar-room
              class="flex grow"
              [room]="room"
              [scope]="scope()"
            />
          </div>
        }

        @if (isTimeBarVisible() && rooms().length) {
          <div
            class="bg-primary-500 pointer-events-none absolute top-0 bottom-0 z-20 w-0.5 -translate-x-1/2"
            [style.left.px]="
              roomsNameContainerWidth() +
              (timeBarLeftOffsetInPercentage() * calendarWidth()) / 100
            "
          >
            <div
              class="bg-primary-500 absolute -top-1 left-1/2 size-2.5 -translate-x-1/2 rounded-full"
            ></div>
          </div>
        }

        @if (loadingRooms() === false && (loadingBookings$ | async)) {
          <div
            class="absolute inset-0 z-30 flex items-center justify-center bg-white/60"
          >
            <app-loader></app-loader>
          </div>
        }
      </div>
    </div>
  `,
  standalone: false,
})
export class BookingsCalendarViewComponent
  extends DestroyableComponent
  implements OnInit, AfterViewInit
{
  platformId = inject(PLATFORM_ID);
  scope = input<BookingsScope>();
  @ViewChild('calendarContainer') calendarContainer: ElementRef;
  @ViewChild('roomsNameContainer') roomsNameContainer: ElementRef;
  loadingRooms: Signal<boolean> = this.store.selectSignal(
    fromStore.selectRoomsLoading,
  );
  loadingBookings$: Observable<boolean>;
  noRoomsFilters$: Observable<boolean>;
  emptyRoomsText$: Observable<string>;
  private calendarRooms = inject(CalendarRoomsService);
  activeRooms = this.calendarRooms.activeRooms;
  rooms: Signal<Room[]> = computed(() =>
    this.calendarRooms.shownRooms(this.scope()),
  );
  displayMode: Signal<BookingsDisplayMode>;
  calendarDate: Signal<Date>;
  isTimeBarVisible: Signal<boolean>;
  currentWeekDates: Signal<string[]>;
  calendarWidth = signal(0);
  roomsNameContainerWidth = signal(0);
  dayHourLabels: Signal<{ label: string; left: number }[]> = computed(() => {
    const numberOfBlocks = this.numberOfBlocksPerDay(this.calendarWidth());
    const hoursPerBlock = 24 / numberOfBlocks;
    return Array.from({ length: numberOfBlocks - 1 }, (_, index) => ({
      label: `${(index + 1) * hoursPerBlock}:00`,
      left: ((index + 1) / numberOfBlocks) * 100,
    }));
  });
  timeBarLeftOffsetInPercentage: Signal<number>;
  isRoomsMenuOpen = signal(false);

  BookingsDisplayMode = BookingsDisplayMode;
  BookingStatus = BookingStatus;
  RoomPermission = RoomPermission;

  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store,
    private modalService: ModalService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    /**
     * Si écran <= md on passe en mode jour car pas assez de place pour afficher la semaine
     */
    afterNextRender(() => {
      const mql = window.matchMedia('(max-width: 768px)');
      if (mql.matches) {
        this.store.dispatch(
          bookingsActions.setDisplayMode({
            displayMode: BookingsDisplayMode.Day,
            scope: this.scope(),
          }),
        );
      }
      mql.addEventListener('change', (e) => {
        if (e.matches) {
          this.store.dispatch(
            bookingsActions.setDisplayMode({
              displayMode: BookingsDisplayMode.Day,
              scope: this.scope(),
            }),
          );
        }
      });
    });
  }

  ngOnInit() {
    this.loadingBookings$ = this.store.select(
      fromStore.selectBookingsLoading(this.scope()),
    );
    this.noRoomsFilters$ = this.store
      .select(fromStore.selectBookingsRoomsFilter(this.scope()))
      .pipe(map((filter) => !filter.people && !filter.eventTypeId));
    this.emptyRoomsText$ = this.store
      .select(fromStore.selectBookingsRoomsFilter(this.scope()))
      .pipe(
        map((filter) =>
          !filter.people && !filter.eventTypeId
            ? this.translocoService.translate(
                'bookings.calendar-view.empty-state-no-rooms',
              )
            : this.translocoService.translate(
                'bookings.calendar-view.empty-state-no-results',
              ),
        ),
      );

    this.displayMode = this.store.selectSignal(
      fromStore.selectBookingsDisplayMode(this.scope()),
    );
    this.calendarDate = this.store.selectSignal(
      fromStore.selectBookingsCalendarDate(this.scope()),
    );

    this.timeBarLeftOffsetInPercentage = computed(() =>
      this.displayMode() === BookingsDisplayMode.Week
        ? (differenceInMinutes(
            new Date(),
            startOfWeek(new Date(), { weekStartsOn: 1 }),
          ) /
            (24 * 60 * 7)) *
          100
        : (differenceInMinutes(new Date(), startOfDay(new Date())) /
            (24 * 60)) *
          100,
    );

    this.isTimeBarVisible = computed(
      () =>
        this.displayMode() === BookingsDisplayMode.Day &&
        isSameDay(this.calendarDate(), new Date()),
    );
    this.currentWeekDates = computed(() => {
      const dates: string[] = [];
      if (this.displayMode() === BookingsDisplayMode.Week) {
        for (let i = 0; i < 7; i++) {
          dates.push(
            format(
              addDays(startOfWeek(this.calendarDate(), { weekStartsOn: 1 }), i),
              'yyyy-MM-dd',
            ),
          );
        }
      } else if (this.displayMode() === BookingsDisplayMode.Day) {
        dates.push(format(this.calendarDate(), 'yyyy-MM-dd'));
      }

      return dates;
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      const calendarElement = this.calendarContainer.nativeElement;
      const roomsNameElement = this.roomsNameContainer.nativeElement;
      const resizeObserver = new ResizeObserver(() => {
        this.calendarWidth.set(calendarElement.getBoundingClientRect().width);
        this.roomsNameContainerWidth.set(
          roomsNameElement.getBoundingClientRect().width,
        );
      });

      resizeObserver.observe(calendarElement);
      resizeObserver.observe(roomsNameElement);
      this.destroyRef.onDestroy(() => resizeObserver.disconnect());

      this.changeDetectorRef.detectChanges();
    }
  }

  isToday(date) {
    return isSameDay(new Date(), new Date(date));
  }

  numberOfBlocksPerDay(width: number) {
    const minWidth = 60;

    const hoursPerBlock = [24, 12, 8, 6, 4, 3, 2, 1];

    for (const hours of hoursPerBlock) {
      if (width / hours >= minWidth) {
        return hours;
      }
    }

    return 1;
  }

  createRoom() {
    this.modalService.open(CreateDuplicateRoomModalComponent, {
      title: this.translocoService.translate(
        'bookings.calendar-view.create-room-modal-title',
      ),
      okAction: {
        title: this.translocoService.translate('common.button.save'),
      },
    });
  }
}
